/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import "../assets/css/bootstrap/bootstrap.css";
import "../assets/css/bootstrap/bootstrap-grid.css";
import "../assets/css/bootstrap/bootstrap-reboot.css";
import "../assets/css/backend-plugin.min.css";
import "../assets/vendor/remixicon/fonts/remixicon.css";
import "../assets/vendor/line-awesome/dist/line-awesome/css/line-awesome.min.css";
import "../assets/vendor/@fortawesome/fontawesome-free/css/all.min.css";
import { useTranslate } from "../hooks/LanguageProvider";
import AuthLayout from "../components/auth/AuthLayout";
import PasswordField from "../components/auth/PasswordField";
import PolicyCheckbox from "../components/auth/PolicyCheckbox";
import AuthAlert from "../components/auth/AuthAlert";
import { useSignupForm } from "../hooks/useSignupForm";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

type RoleValue = "supplier" | "customer";

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { translate } = useTranslate();
  const form = useSignupForm({
    backendUrl,
    translate,
    onSuccess: () => navigate("/login"),
  });

  const primaryButtonLabel = form.isVerificationStep
    ? translate("Verify code and create account")
    : "Create Account";

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Enter" && form.signupButtonRef.current) {
        form.signupButtonRef.current.click();
      }
    };
    document.body.addEventListener("keypress", handleKeyPress);
    return () => document.body.removeEventListener("keypress", handleKeyPress);
  }, [form]);
  return (
    <AuthLayout>
      <h2 className="mb-2">
        {translate("Member space")} , {translate("Sign Up")}!
      </h2>
     

      <form className="smart-form client-form client-form-size">
        <div id="resText"></div>

        <div className="row">
          <div className="col-lg-6">
            <label>{translate("Username")}</label>
            <div className="floating-label form-group">
              <input
                className="floating-input form-control"
                type="text"
                placeholder={translate("Enter your name")}
                name="name"
                id="name"
                value={form.username}
                disabled={form.isFormLocked}
                onChange={(e) => form.setUsername(e.target.value)}
              />
            </div>
          </div>

          <div className="col-lg-6">
            <label>{translate("Email")}</label>
            <div className="floating-label form-group">
              <input
                className="floating-input form-control"
                type="email"
                placeholder={translate("Enter your email")}
                name="email"
                id="email"
                value={form.email}
                disabled={form.isFormLocked}
                onChange={(e) => form.setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="col-lg-6">
            <label>{translate("Phone")}</label>
            <div className="floating-label form-group">
              <input
                className="floating-input form-control"
                type="tel"
                placeholder={translate("Enter your phone")}
                name="phone"
                id="phone"
                value={form.phone}
                disabled={form.isFormLocked}
                onChange={(e) => form.setPhone(e.target.value)}
              />
            </div>
          </div>

         <div className="col-lg-6">
            <label>{translate("Role")}</label>
            <div className="floating-label form-group">
              <select
                className="floating-input form-control"
                name="role"
                id="role"
                value={form.role}
                disabled={form.isFormLocked}
                onChange={(e) => form.setRole(e.target.value as RoleValue)}
              >
                <option value="supplier">{translate("Supplier")}</option>
                <option value="customer">{translate("OEM")}</option>
              </select>
            </div>
          </div>

          <PasswordField
            label={translate("Password")}
            id="new-password"
            value={form.password}
            disabled={form.isFormLocked}
            onChange={form.setPassword}
          />

          <PasswordField
            label={translate("Confirm Password")}
            id="confirm-password"
            value={form.confirmPassword}
            disabled={form.isFormLocked}
            onChange={form.setConfirmPassword}
          />

          

          {form.isVerificationStep && (
            <div className="col-lg-12">
              <label>{translate("Verification Code")}</label>
              <div className="floating-label form-group">
                <input
                  className="floating-input form-control"
                  type="text"
                  placeholder={translate("Enter the code sent to your phone")}
                  name="verificationCode"
                  id="verificationCode"
                  value={form.verificationCode}
                  disabled={form.loading}
                  onChange={(e) => form.setVerificationCode(e.target.value)}
                />
              </div>

              {form.verificationMessage && (
                <p className="text-muted mb-1">{form.verificationMessage}</p>
              )}

              <p className="text-muted mb-3">
                {translate("Attempts remaining")}: {form.attemptsLeft}
              </p>
            </div>
          )}

          <div className="text-center  mb-1 mt-4">
            <button
              type="button"
              className="btn btn-primary col-lg-12 mx-auto d-block"
              onClick={form.submit}
              ref={form.signupButtonRef}
              disabled={form.loading}
            >
              {primaryButtonLabel}
            </button>
          </div>

          {form.isVerificationStep && (
            <>
              <div className="text-center mt-2">
                <button
                  type="button"
                  className="btn btn-outline-primary col-lg-12 mx-auto d-block"
                  onClick={form.resendVerificationCode}
                  disabled={form.loading}
                >
                  {translate("Resend code")}
                </button>
              </div>

              <div className="text-center mt-2">
                <button
                  type="button"
                  className="btn btn-light col-lg-12 mx-auto d-block"
                  onClick={form.cancelVerification}
                  disabled={form.loading}
                >
                  {translate("Change phone number")}
                </button>
              </div>
            </>
          )}

          <PolicyCheckbox
            checked={form.agreePolicy}
            onChange={form.setAgreePolicy}
            translate={translate}
            disabled={form.isFormLocked}
          />

          {form.loading && (
            <div className="text-center">
              <BeatLoader color={"#123abc"} loading={form.loading} size={12} />
            </div>
          )}
        </div>

        <p className="text-center mt-2">
          <span>{translate("Already have an account?")}</span>
          <Link to="/login">
            <span>&nbsp;{translate("Sign in instead")}</span>
          </Link>
        </p>

        <div id="signup-phone-recaptcha"></div>
        <AuthAlert message={form.alertMessage} />
      </form>
    </AuthLayout>
  );
};

export default SignUp;
